/**
 * The geometry the Smart Pen's shape recogniser reasons with (SRS C5).
 *
 * Pure maths in canvas coordinates: a list of points goes in, numbers come out.
 * Nothing here knows about a canvas, a pointer or a board, which is what lets
 * "this circle is round enough" be something a test can prove.
 *
 * Self-contained on purpose. SRS C5.1 requires the recogniser to be replaceable
 * without touching the pen engine, so it borrows no helper from it.
 */

import type { Point } from '@opal/shared';

/** The box a set of points sits in, in canvas units. */
export interface Bounds {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/** How many evenly spaced samples every stroke is reduced to before scoring. */
export const EVEN_SAMPLE_COUNT = 64;

/** Ends closer together than this share of the path length count as joined up. */
const CLOSED_GAP_RATIO = 0.25;

/** Samples nearer than this to the one before carry no information. */
const MIN_STEP = 1e-6;

/** A stroke measured once, so that no scorer has to walk it again. */
export interface StrokePath {
  /** The stroke with its stationary samples dropped. */
  readonly points: readonly Point[];
  /** The stroke at even spacing — what every scorer actually compares against. */
  readonly even: readonly Point[];
  /** `even` without its closing duplicate, for walking a shape round its rim. */
  readonly loop: readonly Point[];
  readonly length: number;
  readonly bounds: Bounds;
  readonly diagonal: number;
  readonly centroid: Point;
  /** Distance from where the hand went down to where it lifted. */
  readonly gap: number;
  /** True when the ends meet closely enough for the stroke to enclose something. */
  readonly closed: boolean;
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

export function pathLength(points: readonly Point[]): number {
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    if (previous && current) total += distance(previous, current);
  }
  return total;
}

export function boundsOf(points: readonly Point[]): Bounds {
  let left = Number.POSITIVE_INFINITY;
  let top = Number.POSITIVE_INFINITY;
  let right = Number.NEGATIVE_INFINITY;
  let bottom = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    left = Math.min(left, point.x);
    top = Math.min(top, point.y);
    right = Math.max(right, point.x);
    bottom = Math.max(bottom, point.y);
  }
  return { left, top, right, bottom };
}

export function centroidOf(points: readonly Point[]): Point {
  if (points.length === 0) return { x: 0, y: 0 };
  let x = 0;
  let y = 0;
  for (const point of points) {
    x += point.x;
    y += point.y;
  }
  return { x: x / points.length, y: y / points.length };
}

/** Drops the pile-up of identical samples a hand resting on the tablet produces. */
function dedupe(points: readonly Point[]): Point[] {
  const kept: Point[] = [];
  for (const point of points) {
    const last = kept[kept.length - 1];
    if (last && distance(last, point) <= MIN_STEP) continue;
    kept.push({ x: point.x, y: point.y });
  }
  return kept;
}

/**
 * The same path, redrawn with every sample the same distance from the last.
 *
 * A stroke arrives with samples bunched where the hand slowed down. Scoring that
 * directly would weigh slow corners far more heavily than fast edges; even spacing
 * takes the hand's speed out of the answer.
 */
export function resample(points: readonly Point[], count: number): Point[] {
  const first = points[0];
  const last = points[points.length - 1];
  const total = pathLength(points);
  if (!first || !last || total <= 0 || count < 2) return [];

  const step = total / (count - 1);
  const out: Point[] = [first];
  let cursor: Point = first;
  let index = 1;
  let carried = 0;

  while (index < points.length && out.length < count - 1) {
    const next = points[index];
    if (!next) break;
    const span = distance(cursor, next);
    if (carried + span >= step) {
      const ratio = (step - carried) / span;
      const placed = {
        x: cursor.x + (next.x - cursor.x) * ratio,
        y: cursor.y + (next.y - cursor.y) * ratio,
      };
      out.push(placed);
      cursor = placed;
      carried = 0;
      continue;
    }
    carried += span;
    cursor = next;
    index += 1;
  }

  while (out.length < count) out.push(last);
  return out;
}

/** A light moving average. Corner finding wants the wobble gone, not the shape. */
export function smooth(points: readonly Point[], passes: number): Point[] {
  let current: Point[] = points.map((point) => ({ x: point.x, y: point.y }));
  for (let pass = 0; pass < passes; pass += 1) {
    const source = current;
    current = source.map((point, index) => {
      const before = source[index - 1] ?? point;
      const after = source[index + 1] ?? point;
      return {
        x: (before.x + point.x * 2 + after.x) / 4,
        y: (before.y + point.y * 2 + after.y) / 4,
      };
    });
  }
  return current;
}

export function distanceToSegment(point: Point, start: Point, end: Point): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const span = dx * dx + dy * dy;
  if (span === 0) return distance(point, start);
  const along = ((point.x - start.x) * dx + (point.y - start.y) * dy) / span;
  const clamped = Math.max(0, Math.min(1, along));
  return Math.hypot(point.x - (start.x + clamped * dx), point.y - (start.y + clamped * dy));
}

/** Distance from a point to the nearest edge of a path, closing the path when asked. */
export function distanceToPath(point: Point, path: readonly Point[], closed: boolean): number {
  let best = Number.POSITIVE_INFINITY;
  const edges = closed ? path.length : path.length - 1;
  for (let index = 0; index < edges; index += 1) {
    const start = path[index];
    const end = path[(index + 1) % path.length];
    if (!start || !end) continue;
    best = Math.min(best, distanceToSegment(point, start, end));
  }
  return best;
}

/** How far, on average, the drawn stroke sits from the clean shape being tested. */
export function meanDistanceTo(
  points: readonly Point[],
  path: readonly Point[],
  closed: boolean,
): number {
  if (points.length === 0 || path.length < 2) return Number.POSITIVE_INFINITY;
  let total = 0;
  for (const point of points) total += distanceToPath(point, path, closed);
  return total / points.length;
}

/** An angle brought back into the range -PI to PI. */
export function normaliseAngle(angle: number): number {
  let value = angle;
  while (value > Math.PI) value -= 2 * Math.PI;
  while (value < -Math.PI) value += 2 * Math.PI;
  return value;
}

/** The turn the path makes at each sample, in radians, measured over `span` samples. */
export function turnAngles(path: readonly Point[], span: number, cyclic: boolean): number[] {
  const count = path.length;
  const turns = new Array<number>(count).fill(0);
  for (let index = 0; index < count; index += 1) {
    const behind = cyclic ? (index - span + count) % count : index - span;
    const ahead = cyclic ? (index + span) % count : index + span;
    const here = path[index];
    const before = path[behind];
    const after = path[ahead];
    if (!here || !before || !after) continue;
    const incoming = Math.atan2(here.y - before.y, here.x - before.x);
    const outgoing = Math.atan2(after.y - here.y, after.x - here.x);
    turns[index] = normaliseAngle(outgoing - incoming);
  }
  return turns;
}

export interface Corner {
  readonly index: number;
  /** How far the path turns there, in radians. */
  readonly turn: number;
}

/**
 * The corners of a stroke, sharpest first.
 *
 * Every sample near a corner turns, so the sharpest of each cluster wins and the
 * rest are suppressed. Without that, one drawn corner reads as four.
 *
 * They come back ranked rather than counted because how sharp a drawn corner reads
 * varies with how fast the hand rounded it, and a hexagon whose sixth corner was
 * taken gently is still a hexagon. Deciding how many corners a shape has is left to
 * whoever can also check whether the resulting shape fits.
 */
export function findCorners(path: readonly Point[], minTurn: number, cyclic: boolean): Corner[] {
  const span = Math.max(2, Math.round(path.length / 16));
  const strength = turnAngles(smooth(path, 2), span, cyclic).map(Math.abs);
  const corners: Corner[] = [];

  for (let index = 0; index < strength.length; index += 1) {
    const here = strength[index] ?? 0;
    if (here < minTurn) continue;
    let sharpest = true;
    for (let offset = -span; offset <= span; offset += 1) {
      if (offset === 0) continue;
      const at = cyclic ? (index + offset + strength.length) % strength.length : index + offset;
      const other = strength[at] ?? 0;
      if (other > here || (other === here && at < index)) {
        sharpest = false;
        break;
      }
    }
    if (sharpest) corners.push({ index, turn: here });
  }
  return corners.sort((a, b) => b.turn - a.turn);
}

/** 1 at or under `perfect`, 0 at or over `worst`, a straight slope between them. */
export function scoreFromError(error: number, perfect: number, worst: number): number {
  if (!Number.isFinite(error)) return 0;
  if (error <= perfect) return 1;
  if (error >= worst) return 0;
  return 1 - (error - perfect) / (worst - perfect);
}

/** How far a value falls outside a band. Zero while it is inside. */
export function outsideBand(value: number, low: number, high: number): number {
  if (value < low) return low - value;
  if (value > high) return value - high;
  return 0;
}

/** The frame a closed stroke sits in: where it is centred, and which way it is stretched. */
export interface Axes {
  readonly centre: Point;
  /** Unit vector along the longer axis. */
  readonly major: Point;
  /** Unit vector across it. */
  readonly minor: Point;
  readonly halfMajor: number;
  readonly halfMinor: number;
  /** Radians, measured from the x axis. */
  readonly rotation: number;
}

export function principalAxes(points: readonly Point[]): Axes {
  const middle = centroidOf(points);
  let sxx = 0;
  let sxy = 0;
  let syy = 0;
  for (const point of points) {
    const dx = point.x - middle.x;
    const dy = point.y - middle.y;
    sxx += dx * dx;
    sxy += dx * dy;
    syy += dy * dy;
  }

  const rotation = 0.5 * Math.atan2(2 * sxy, sxx - syy);
  const major = { x: Math.cos(rotation), y: Math.sin(rotation) };
  const minor = { x: -major.y, y: major.x };
  const extent = extentIn(points, middle, major, minor);

  return {
    centre: {
      x: middle.x + extent.offsetU * major.x + extent.offsetV * minor.x,
      y: middle.y + extent.offsetU * major.y + extent.offsetV * minor.y,
    },
    major,
    minor,
    halfMajor: extent.halfU,
    halfMinor: extent.halfV,
    rotation,
  };
}

interface Extent {
  readonly offsetU: number;
  readonly offsetV: number;
  readonly halfU: number;
  readonly halfV: number;
}

/** How far the points reach along and across a pair of axes. */
function extentIn(points: readonly Point[], origin: Point, major: Point, minor: Point): Extent {
  let minU = Number.POSITIVE_INFINITY;
  let maxU = Number.NEGATIVE_INFINITY;
  let minV = Number.POSITIVE_INFINITY;
  let maxV = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    const dx = point.x - origin.x;
    const dy = point.y - origin.y;
    const u = dx * major.x + dy * major.y;
    const v = dx * minor.x + dy * minor.y;
    minU = Math.min(minU, u);
    maxU = Math.max(maxU, u);
    minV = Math.min(minV, v);
    maxV = Math.max(maxV, v);
  }
  return {
    offsetU: (maxU + minU) / 2,
    offsetV: (maxV + minV) / 2,
    halfU: (maxU - minU) / 2,
    halfV: (maxV - minV) / 2,
  };
}

/**
 * Cuts off the tail a hand draws past the point it started at.
 *
 * Closing a shape by carrying on over the first stroke is the normal way to draw
 * one. Left in, that overlap folds back on itself and reads as an extra corner
 * right where the shape began, which is enough to lose a hexagon a side.
 */
function trimOvershoot(even: readonly Point[], length: number): Point[] {
  const start = even[0];
  if (!start) return even.slice();

  const from = Math.floor(even.length * 0.75);
  let cut = even.length - 1;
  let closest = Number.POSITIVE_INFINITY;
  for (let index = from; index < even.length; index += 1) {
    const point = even[index];
    if (!point) continue;
    const away = distance(point, start);
    if (away < closest) {
      closest = away;
      cut = index;
    }
  }

  const end = cut < even.length - 1 && closest <= length * 0.05 ? cut : even.length - 1;
  return even.slice(0, end);
}

/** Measures a stroke once. Null when there is not enough of it to say anything. */
export function measureStroke(raw: readonly Point[]): StrokePath | null {
  const points = dedupe(raw);
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last || points.length < 4) return null;

  const length = pathLength(points);
  if (length <= 0) return null;

  const even = resample(points, EVEN_SAMPLE_COUNT);
  if (even.length < EVEN_SAMPLE_COUNT) return null;

  const bounds = boundsOf(points);
  const gap = distance(first, last);
  const closed = gap <= length * CLOSED_GAP_RATIO;

  return {
    points,
    even,
    loop: closed ? trimOvershoot(even, length) : even,
    length,
    bounds,
    diagonal: Math.hypot(bounds.right - bounds.left, bounds.bottom - bounds.top),
    centroid: centroidOf(even),
    gap,
    closed,
  };
}
